import React, { useCallback, useMemo } from 'react';

import {
  Card,
  Text,
  Image,
  ImageResizeOptions,
  PaddingModes,
} from 'wix-ui-tpa';

import { useTranslation } from '@wix/yoshi-flow-editor';
import { useStyles } from '@wix/tpa-settings/react';

import { useAppDispatch } from '@/store';
import { addToCart } from '@/features';
import { EXPERIMENTS } from '@/constants';

import styleParams from 'AiAssistantWidget/stylesParams';
import { useAppExperiments } from 'AiAssistantWidget/experiments';

import { ProductGallery } from '../ProductGallery';
import { ChatCard } from '../ChatCard';
import { PayloadAction, PayloadCmpProps } from '../ChatMessagePayload/types';
import { ImageBlock } from '../ImageBlock';
import {
  LinkButton,
  WidgetButton,
  WidgetButtonPriority,
  WidgetButtonSize,
} from '../WidgetButton';

import { classes, st } from './Product.st.css';

type ProductProps = PayloadCmpProps & {
  images?: string[];
  title?: string;
  subtitle?: string;
  description?: string;
  price?: string;
  originalPrice?: string;
  link?: string;
  noImagesGallery?: boolean;
};

export const Product: React.FC<
  ProductProps & React.HTMLAttributes<HTMLElement>
> = ({
  isSmall,
  images,
  title,
  subtitle,
  description,
  price,
  originalPrice,
  link,
  noImagesGallery,
  actions,
  payloadId,
  ...rootElementAttrs
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { experiments } = useAppExperiments();
  const hasInfoBlock = useMemo(
    () => Boolean(title || subtitle || description || price),
    [description, price, subtitle, title],
  );
  const styles = useStyles();

  const shouldRenderOptions = experiments.enabled(
    EXPERIMENTS.RENDER_AI_MESSAGE_OPTIONS,
  );

  const onClickAddToCart = useCallback(() => {
    if (!payloadId) {
      return;
    }
    dispatch(addToCart({ id: payloadId, name: title ?? payloadId }));
  }, [payloadId, dispatch, title]);

  return (
    <ChatCard
      {...rootElementAttrs}
      className={st(classes.root, rootElementAttrs.className)}
      cardClassName={st(classes.card)}
      isSmall={Boolean(isSmall)}
      tabIndex={0}
    >
      <Card.Container className={st(classes.section, classes.sectionGallery)}>
        {noImagesGallery ? (
          Boolean(images) && (
            <ImageBlock
              className={st(classes.imageBlock)}
              aspectRatio={styles.get(styleParams.productImageAspectRatio)}
            >
              <Image
                src={images![0]}
                resize={ImageResizeOptions.cover}
                width={226}
                aspectRatio={styles.get(styleParams.productImageAspectRatio)}
              />
            </ImageBlock>
          )
        ) : (
          <ProductGallery images={images ?? []} isSmall={Boolean(isSmall)} />
        )}
      </Card.Container>

      {hasInfoBlock && (
        <Card.Container className={st(classes.section)}>
          {title && <Text className={st(classes.title)}>{title}</Text>}
          {subtitle && (
            <Text className={st(classes.description)}>{subtitle}</Text>
          )}
          {description && (
            <Text className={st(classes.description)}>{description}</Text>
          )}
          <div className={st(classes.priceContainer)}>
            {price && <Text className={st(classes.description)}>{price}</Text>}
            {originalPrice && (
              <Text className={st(classes.description, classes.originalPrice)}>
                {originalPrice}
              </Text>
            )}
          </div>
        </Card.Container>
      )}

      {link && (
        <Card.Container className={st(classes.section, classes.sectionLink)}>
          <div className={st(classes.actionButtons)}>
            <LinkButton
              fullWidth
              link={link}
              title={t('app.widget.product.viewItem')}
              reportNavigationToAssistant={
                payloadId && actions?.includes(PayloadAction.VIEW)
                  ? { id: payloadId }
                  : undefined
              }
              // This prop is needed for ellipsis behavior
              paddingMode={PaddingModes.explicitPaddings}
              priority={WidgetButtonPriority.basic}
            />
            {shouldRenderOptions &&
              actions?.includes(PayloadAction.ADD_TO_CART) &&
              payloadId && (
                <WidgetButton
                  text={t('app.widget.product.addToCart')}
                  onClick={onClickAddToCart}
                  priority={WidgetButtonPriority.basicSecondary}
                  paddingMode={PaddingModes.explicitPaddings}
                  fullWidth
                />
              )}
          </div>
        </Card.Container>
      )}
    </ChatCard>
  );
};
